import type { BulkAPIErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

export function useStockLevel(
  entityId: string,
  options?: { enabled?: boolean; fallbackData?: Record<number, boolean> }
): {
  inStock: boolean | undefined;
  stockLevels: Record<number, boolean> | undefined;
} {
  const enabled = options?.enabled ?? true;
  const [inStock, setInStock] = useState<boolean | undefined>();

  const fetcher = async (url) => {
    const res = await fetch(url);

    // If the status code is not in the range 200-299,
    // we still try to parse and throw it.
    if (res.status !== 200) {
      const {
        error: { message },
      } = (await res.json()) as BulkAPIErrorResponse;
      const error = new Error('Unable to fetch stock levels');
      error.message = message.join('. ');
      throw error;
    }

    const { data } = await res.json();
    return data;
  };

  // Get stock levels
  const { data: stockLevels } = useSWR<Record<number, boolean>>(
    enabled && entityId ? `/api/stock-level?id=${entityId}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 30000,
      fallbackData: options?.fallbackData, // allow server-provided mapping to be used as initial data
    }
  );

  useEffect(() => {
    if (stockLevels) {
      // If at least one SKU is in stock, then product in stock
      const res = Object.values(stockLevels).some((val) => val);
      setInStock(res);
    } else {
      setInStock(undefined);
    }
  }, [stockLevels]);

  return { inStock, stockLevels };
}
